/**
 * Shared customer (הנהלת חשבונות → לקוחות) types and option lists.
 *
 * The enum *values are the Hebrew labels themselves* — this keeps the Excel
 * round-trip trivial (the cell value IS the stored value) and lets filter /
 * dropdown options reuse the same list everywhere.
 *
 * NOTE: the backend mirrors these lists in
 * `apps/backend/src/api/private/customers/customers.constants.ts` (it must stay
 * in sync). Keep both in step when editing the domain vocabulary.
 */

import { MailingChannel } from './mailing.types';

export interface SelectOptionLite {
  label: string;
  value: string;
}

const toOptions = (values: readonly string[]): SelectOptionLite[] => values.map(v => ({ label: v, value: v }));

// --- צורת תשלום ---
export const PAYMENT_METHODS = ['הו"ק', 'אשראי', 'לקוחות', 'לקוחות מחושבים'] as const;
export const PAYMENT_METHOD_OPTIONS = toOptions(PAYMENT_METHODS);

// --- גביה ---
export const COLLECTION_AUTO = 'גביה אוטומטית';
export const COLLECTION_TO_COLLECT = 'לגביה';
export const COLLECTION_NO_COLLECT = 'לא לגביה';
export const COLLECTION_CREDIT = 'לקוח בזכות';
export const COLLECTIONS = [COLLECTION_AUTO, COLLECTION_TO_COLLECT, COLLECTION_NO_COLLECT, COLLECTION_CREDIT] as const;
export const COLLECTION_OPTIONS = toOptions(COLLECTIONS);

// --- סטטוס טיפול (רלוונטי רק כשגביה = "לגביה") ---
export const TREATMENT_STATUSES = ['לטיפול', 'חוב טופל', 'נחתם הוראת קבע', 'לא אותר באתר', 'לא זמין'] as const;
export const TREATMENT_STATUS_OPTIONS = toOptions(TREATMENT_STATUSES);

/**
 * The two treatment statuses that mean the debt was settled (חוב טופל / נחתם הוראת קבע).
 *
 * They do **not** move the collection status: a customer who paid stays "לגביה" with "חוב טופל" —
 * that pair is exactly what the dashboard measures ("how much of the debt was handled this month").
 * The collection status is derived from the monthly Excel alone.
 */
export const TREATMENT_STATUSES_SETTLED: readonly string[] = [TREATMENT_STATUSES[1], TREATMENT_STATUSES[2]];

/** Chip colour per treatment status, as **theme palette keys** (mirrors `SITE_STATUS_COLOR`). */
export const TREATMENT_STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  [TREATMENT_STATUSES[0]]: 'warning', // לטיפול
  [TREATMENT_STATUSES[1]]: 'success', // חוב טופל
  [TREATMENT_STATUSES[2]]: 'success', // נחתם הוראת קבע
  [TREATMENT_STATUSES[3]]: 'default', // לא אותר באתר
  [TREATMENT_STATUSES[4]]: 'default' // לא זמין
};

// --- סטטוס אתר ---
export const SITE_STATUS_OPEN = 'פתוח';
export const SITE_STATUS_TO_CLOSE = 'לסגירה';
export const SITE_STATUS_CLOSED = 'סגור';
export const SITE_STATUS_TO_OPEN = 'לפתיחה';
/** Explicit opt-out — never auto-blocked by the collection import. */
export const SITE_STATUS_VIP = 'לקוח VIP (לא לחסימה)';
export const SITE_STATUSES = [
  SITE_STATUS_OPEN,
  SITE_STATUS_TO_CLOSE,
  SITE_STATUS_CLOSED,
  SITE_STATUS_TO_OPEN,
  SITE_STATUS_VIP
] as const;
export const SITE_STATUS_OPTIONS = toOptions(SITE_STATUSES);

/** Chip colour per site status, as **theme palette keys** — this project never hardcodes a colour. */
export const SITE_STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  [SITE_STATUS_OPEN]: 'success',
  [SITE_STATUS_TO_CLOSE]: 'warning',
  [SITE_STATUS_CLOSED]: 'error',
  [SITE_STATUS_TO_OPEN]: 'info',
  [SITE_STATUS_VIP]: 'default'
};

// --- סוג לקוח ---

/**
 * סוג הלקוח שכל לקוח ייצור (`/planning/customers`) נכנס תחתיו במסך הלקוחות.
 *
 * A named export and not just a string in the list below, because it is the value the production
 * customers module *writes*: `ensureCustomerCard` files every card it opens under this type, and a
 * literal typed twice would drift the moment somebody rewords the label. Adding it to
 * `CUSTOMER_TYPES` is what puts it in the customers screen's filter, in the card form's dropdown
 * and in the Excel template's data validation — `allCustomerTypes()` builds all three from this
 * list, so a card the system wrote would otherwise fail re-import as "ערך לא חוקי".
 */
export const CUSTOMER_TYPE_PRODUCTION = 'לקוח חיצוני - רכש';

export const CUSTOMER_TYPES = [
  'בית חב"ד',
  'לקוחות רגילים',
  'קהילות',
  'את"ה',
  'בית חב"ד חו"ל',
  'חב"ד בעליה',
  'חב"ד ברפואה - VIP',
  'מוסדות חינוך',
  'בית רפואה',
  'לקוח נלווה',
  'פרסום בשיחת השבוע-רוסית',
  'פעילים רשת',
  'מבצע נשק',
  'פרסום בשיחת השבוע',
  'מועדון המעשים הטובים',
  'עלונים להפצה',
  CUSTOMER_TYPE_PRODUCTION
] as const;
export const CUSTOMER_TYPE_OPTIONS = toOptions(CUSTOMER_TYPES);

/**
 * Where a card came from.
 *
 * `priority` is every card the office manages — imported from Priority, collected from, billed.
 * `store` is a buyer a shop order opened automatically. `production` is a לקוח ייצור — an
 * organisation we manufacture *for*, whose card is opened by the production customers screen.
 *
 * **This is a structural fact, and it is the only thing the collection dashboard may filter on.**
 * `customerType` also distinguishes them (a store card carries its store's label, a production card
 * carries `CUSTOMER_TYPE_PRODUCTION`), but that is an editable business label: somebody retyping it
 * in the customers screen would quietly move a one-off buyer into the money counts.
 * `getOverviewStats` builds every pie and every rep counter from the whole customer base, so the
 * filter has to sit on a column nobody edits.
 *
 * `production` is deliberately **not** `priority`: a production customer has no Priority id, no
 * `collection` and no `paymentMethod`, so counting it in the גביה dashboard would inflate the
 * customer total and add a `null` slice to all four pies — the exact failure `store` was given its
 * own source to prevent.
 */
export const CUSTOMER_SOURCES = ['priority', 'store', 'production', 'landing'] as const;
export type CustomerSource = (typeof CUSTOMER_SOURCES)[number];

export const CUSTOMER_SOURCE_LABELS: Record<CustomerSource, string> = {
  priority: 'לקוח מל״ש',
  store: 'קונה מחנות',
  production: 'לקוח ייצור',
  landing: 'דף נחיתה'
};

// --- Entities (API shapes) ---
export interface CustomerTag {
  id: number;
  name: string;
  /** System tags are created by a migration and can be neither renamed nor deleted. */
  isSystem?: boolean;
  /**
   * How many customers carry this tag. Returned only by the tags list endpoint (the tags nested on
   * a customer don't carry it), for the "ניהול תגיות" dialog.
   */
  customersCount?: number;
}

// --- תגית VIP (תגית מערכת) ---

/**
 * The VIP system tag. A customer carrying it is pinned to `VIP_LOCKED_STATUSES`: the site is never
 * blocked, whatever the monthly Excel says. Collection is *not* pinned — a VIP who owes is לגביה
 * and gets a treatment status like anyone else; only the site stays open. The tag — not the
 * status — is the source of truth, which is why it is a tag and not just a site status: statuses
 * are recomputed every month, tags are not.
 */
export const VIP_TAG_NAME = 'VIP';

/** The status the VIP tag pins, for as long as it is attached. */
export const VIP_LOCKED_STATUSES = {
  siteStatus: SITE_STATUS_VIP
} as const;

/** Whether a customer's tag list carries the VIP tag — i.e. its statuses are locked. */
export const isVipTagged = (tags?: readonly { name: string }[] | null): boolean =>
  !!tags?.some(tag => tag.name === VIP_TAG_NAME);

/**
 * The tags-filter value meaning "no tag at all". Sent in the same comma-joined list as tag ids, and
 * OR-ed with them like any other choice (`tags=none,3` → untagged customers plus those tagged 3).
 */
export const TAG_FILTER_NONE = 'none';
export const TAG_FILTER_NONE_LABEL = 'ללא תגית';

/** Soft hyphen, zero-width chars and bidi controls — invisible, so they must not defeat a compare. */
const INVISIBLE_MARKS = /[\u00AD\u200B-\u200F\u202A-\u202E\u2060\uFEFF]/g;

/**
 * One iShop address per entry, no repeats.
 *
 * The stored array can't be trusted to hold one address per element: the sheets these came from put
 * several addresses in a single cell separated by spaces or semicolons (the importer only ever split
 * on commas), so a single element like "a@x.com a@x.com b@x.com" is normal. Splitting on every
 * plausible separator is therefore what makes "one per line" and de-duplication actually work.
 *
 * Comparison is case-insensitive and ignores the invisible bidi/zero-width marks that ride along
 * with copied Hebrew-sheet text; the first spelling seen is the one kept.
 */
export const normalizeIshopEmails = (values?: readonly string[] | null): string[] => {
  const seen = new Set<string>();
  const emails: string[] = [];
  for (const value of values ?? []) {
    for (const email of (value ?? '').split(/[\s,;]+/)) {
      const clean = email.replace(INVISIBLE_MARKS, '').trim();
      if (!clean) continue;
      const key = clean.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      emails.push(clean);
    }
  }
  return emails;
};

export interface CustomerNote {
  id: number;
  customerId: number;
  body: string;
  authorUserId: number | null;
  authorName?: string | null;
  createdAt: string;
  /**
   * Later than `createdAt` once the note was edited, which is how the card can print "נערך".
   * Optional because a note cached from before the field existed has no value for it.
   */
  updatedAt?: string;
}

/**
 * A task on a customer for one collection month — "להתקשר מחר", as opposed to a permanent note.
 * It lives in its `period` only and is never copied to the next month.
 */
export interface CustomerTask {
  id: number;
  customerId: number;
  customerName: string | null;
  /** The collection month it belongs to, 'YYYY-MM'. */
  period: string;
  body: string;
  /** טופל. */
  isDone: boolean;
  doneAt: string | null;
  doneByUserId: number | null;
  doneByName: string | null;
  authorUserId: number | null;
  authorName: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Field → Hebrew label map used by the customer activity-log diff. */
export const CUSTOMER_FIELD_LABELS: Record<string, string> = {
  priorityId: 'מזהה לקוח (פריורטי)',
  shluchimDashboardId: 'מזהה בדשבורד השלוחים',
  name: 'שם',
  email: 'מייל',
  phone: 'טלפון',
  paymentMethod: 'צורת תשלום',
  collection: 'גביה',
  treatmentStatus: 'סטטוס טיפול',
  handlingUserId: 'נציג מטפל',
  customerType: 'סוג לקוח',
  ishopEmails: 'משתמשים באישופ',
  siteStatus: 'סטטוס אתר',
  tags: 'תגיות'
};

export type CustomerActivityAction = 'create' | 'update' | 'delete' | 'task_created' | 'task_updated' | 'task_deleted';
export type CustomerActivitySource = 'manual' | 'import';

export interface CustomerActivityChange {
  field: string;
  label: string;
  from: string | null;
  to: string | null;
}

export interface CustomerActivity {
  id: number;
  customerId: number;
  userId: number | null;
  actorName?: string | null;
  action: CustomerActivityAction;
  source: CustomerActivitySource;
  changes: CustomerActivityChange[];
  createdAt: string;
}

/**
 * A message that went out to the customer (SMS / מייל), as shown in לוג פעילות.
 *
 * Not a `CustomerActivity` row: the send is already recorded once, in
 * MailingMessages + MailingRecipients, and copying it into the audit table would
 * leave two records of the same fact free to drift. The log endpoint merges the
 * two sources by time instead.
 */
export interface CustomerMessageActivity {
  /** The MailingRecipient id — unique only within this kind, so key on `kind` + `id`. */
  id: number;
  customerId: number;
  /** Who sent it — the user behind the campaign, not the recipient. */
  actorName?: string | null;
  channel: MailingChannel;
  subject: string | null;
  /** The text as this recipient received it — {tokens} already substituted. */
  body: string;
  status: 'sent' | 'failed';
  errorMessage: string | null;
  /** The phone or address it actually went to. */
  sentTo: string | null;
  createdAt: string;
}

/**
 * One entry of the לוג פעילות feed — either a change to the card or a message sent to
 * the customer. Discriminated on `kind` because the two come from different tables and
 * render as different cards.
 */
export type CustomerActivityEntry =
  | ({ kind: 'change' } & CustomerActivity)
  | ({ kind: 'message' } & CustomerMessageActivity);

// --- ייבוא לקוחות מאקסל ---

/**
 * Why a sheet row never reached the database. `priorityId` is the import's identity key, so a row
 * without one cannot be matched *or* safely created (re-uploading the file would duplicate it).
 */
export type CustomerImportSkipReason = 'missing_priority_id' | 'missing_name' | 'duplicate_in_file';

export const CUSTOMER_IMPORT_SKIP_LABELS: Record<CustomerImportSkipReason, string> = {
  missing_priority_id: 'חסר מזהה לקוח (פריורטי)',
  missing_name: 'לקוח חדש ללא שם',
  duplicate_in_file: 'מזהה מופיע יותר מפעם אחת בקובץ (נלקחה השורה האחרונה)'
};

export interface CustomerImportSkippedRow {
  /** The row number in the sheet, so the user can go fix that exact line. */
  row: number;
  priorityId?: string;
  reason: CustomerImportSkipReason;
}

export interface CustomerImportResult {
  created: number;
  /** Matched customers whose fields actually changed. */
  updated: number;
  /** Matched customers the sheet had nothing new for. */
  unchanged: number;
  skippedCount: number;
  /** The first few skipped rows only — a whole broken file would otherwise be sent back. */
  skipped: CustomerImportSkippedRow[];
  /** The upload this run was logged as, in היסטוריית העלאות אקסל. */
  batchId?: number;
}

// --- Customers Excel upload log (היסטוריית העלאות אקסל) ---

export const CUSTOMER_IMPORT_BATCH_ACTIVE = 'active';
export const CUSTOMER_IMPORT_BATCH_UNDONE = 'undone';

/** One customers-sheet upload, with the row counts it produced and who ran it. */
export interface CustomerImportBatch {
  id: number;
  fileName: string | null;
  userId: number | null;
  createdCount: number;
  updatedCount: number;
  unchangedCount: number;
  skippedCount: number;
  status: string; // CUSTOMER_IMPORT_BATCH_*
  createdAt: string;
  /**
   * Only the newest upload that is still in effect can be reversed — undoing an older one would
   * roll back over everything the uploads after it wrote.
   */
  canUndo?: boolean;
  /** Who uploaded it (joined for display). */
  user?: { id: number; firstName: string; lastName: string } | null;
}

/** What an undo actually managed to reverse — the dialog reports it back. */
export interface CustomerImportUndoResult {
  /** Customers the upload created, and the undo deleted. */
  deleted: number;
  /** Customers whose fields the undo restored. */
  reverted: number;
  /** Touched customers left as they are, because they were edited by hand after the upload. */
  keptManual: number;
}

export interface Customer {
  id: number;
  priorityId: string | null;
  shluchimDashboardId: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  paymentMethod: string | null;
  collection: string | null;
  treatmentStatus: string | null;
  handlingUserId: number | null;
  handlingUser?: { id: number; firstName: string; lastName: string } | null;
  customerType: string | null;
  ishopEmails: string[];
  /**
   * Addresses a shop checkout supplied that are **not** the card's primary one.
   *
   * The primary `email` is never overwritten by an order: collection mail goes to it, and a
   * checkout on a website silently redirecting it is the failure this field exists to avoid. A
   * different address that arrives with an order is information, though, so it is kept here and
   * noted in the activity log rather than discarded.
   *
   * Distinct from `ishopEmails`, which means "iShop users" and appears under that name in the
   * import template.
   */
  alternateEmails: string[];
  source: CustomerSource;
  siteStatus: string | null;
  tags?: CustomerTag[];
  notesCount?: number;
  /**
   * Consecutive "לגביה" months ending at the customer's most recent collection month — computed by
   * the list query, not stored. See `collectionStreakTone` / `COLLECTION_STREAK_ALERT` in
   * `collection.types.ts` (which can't be imported here: it imports *this* file).
   */
  collectionStreak?: number;
  /**
   * Billed through the מל"ש account but without a Priority customer number — the monthly export
   * leaves that charge out until one is entered. Computed by the list/card query; never blocks
   * anything. Card customers never have it: a card charge is not exported.
   */
  awaitingPriorityId?: boolean;
  createdAt?: string;
  updatedAt?: string;
}
